import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {useRouter} from '@aboutyou/legacy-desktop/src/app/helpers/navigation';
import {PageTrackingContextProvider} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/PageTrackingContext';
import {useTracking} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/useTrackingContextAndClientV6';
import {useTrackingPageUrl} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/useTrackingPageUrl';
import TrackContentPageView from '@aboutyou/legacy-desktop/src/app/helpers/tracking/trackerComponents/TrackContentPageView';
import redialHandle from '@aboutyou/legacy-desktop/src/app/pages/HelpCenter/redialHelpCenterLandingPage';
import {
  useHelpCenterAlternateLink,
  useHelpCenterCanonicalUrl,
} from '@aboutyou/legacy-desktop/src/app/pages/HelpCenter/seo';
import useHelpCenterTranslations from '@aboutyou/legacy-desktop/src/app/pages/HelpCenter/useHelpCenterTranslations';
import {useService} from '@aboutyou/legacy-desktop/src/app/services/utils/context';
import {
  useHelpCenterLandingPageStore,
  useUserStore,
} from '@aboutyou/legacy-desktop/src/app/store/context';
import {useLocale} from '@aboutyou/legacy-desktop/src/app/store/features/locale/useLocale';
import LegacyPage from '@aboutyou/legacy-shared/src/pages/HelpCenter/HelpCenterLandingPage';
import {TrackingPageTypeWithoutId} from '@aboutyou/legacy-tracking-sdk';
import React from 'react';
import redial from 'redial';
import {withLegacyProviders} from '../../LegacyComponentWrapper';

const ConnectedHelpCenterLandingPage: React.FC<
  React.PropsWithChildren
> = () => {
  const translations = useHelpCenterTranslations({});

  const {TrackingClient} = useTracking();
  const {navigateClient} = useRouter();

  const highlightArticles = useHelpCenterLandingPageStore(
    store => store.highlightArticles,
  );
  const highlightQuestions = useHelpCenterLandingPageStore(
    store => store.highlightQuestions,
  );
  const headerImageLarge = useHelpCenterLandingPageStore(
    store => store.headerImageLarge,
  );
  const headerImageSmall = useHelpCenterLandingPageStore(
    store => store.headerImageSmall,
  );
  const sections = useHelpCenterLandingPageStore(store => store.sections);
  const canonical = useHelpCenterCanonicalUrl();
  const {path, search} = useTrackingPageUrl();
  const alternateLink = useHelpCenterAlternateLink(canonical);
  const {helpCenter} = useService();
  const {locale} = useLocale();
  const isVip = useUserStore(store => store.isVip);

  const isShowOldHelpCenterEnabled = useIsFeatureActive(
    'show_old_help_center',
  ).isActive;

  const isCoinsActive = useIsFeatureActive('ff_coins_rollout').isActive;

  return (
    <PageTrackingContextProvider
      path={path}
      search={search}
      type={TrackingPageTypeWithoutId.HelpCenter}
    >
      <TrackContentPageView
        pageTitle={translations.landingPage.seo_page_title}
      />
      <LegacyPage
        isShowOldHelpCenterEnabled={isShowOldHelpCenterEnabled}
        isCoinsActive={isCoinsActive}
        locale={locale}
        sections={sections}
        endpoints={helpCenter}
        canonicalUrl={canonical}
        headerImageLarge={headerImageLarge}
        headerImageSmall={headerImageSmall}
        trackingClient={TrackingClient}
        translations={translations}
        navigateClient={navigateClient}
        highlightQuestions={highlightQuestions}
        highlightArticles={highlightArticles}
        alternateLink={alternateLink}
        isVip={isVip}
      />
    </PageTrackingContextProvider>
  );
};

const PageWithRedial = redial.provideHooks(redialHandle)(
  ConnectedHelpCenterLandingPage,
);

export const LandingPage = withLegacyProviders(PageWithRedial);
